import { getAuthItem, setAuthItem } from "../../../public/utils/authStorage";
import config from "../../../config/config";
import { getCsrfToken } from "../../../public/api/authApi";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";
import { decodeJwtPayload } from "../../../public/utils/jwtRole";

const USER_BUILDER_BASE = `${config.BASE_URL}/user-builder`;
const MUTATION_TIMEOUT_MS = 60_000;

async function refreshCsrfToken() {
  const token = await getCsrfToken();
  if (token) {
    setAuthItem("csrf_token", token);
  }
  return token;
}

async function mutatingAuthFetch(input, init = {}) {
  await refreshCsrfToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MUTATION_TIMEOUT_MS);
  try {
    let res = await authFetch(input, {
      ...init,
      signal: controller.signal,
      headers: { ...buildAuthHeaders(), ...(init.headers || {}) },
    });
    if (res.status === 403) {
      await refreshCsrfToken();
      res = await authFetch(input, {
        ...init,
        signal: controller.signal,
        headers: { ...buildAuthHeaders(), ...(init.headers || {}) },
      });
    }
    return res;
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("Save timed out. Check your connection and try again.");
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function createApiError(res, data, fallbackMessage) {
  const raw = String(data?.message || "").trim();
  const looksLikeHtml = raw.startsWith("<!DOCTYPE") || raw.startsWith("<html");
  const normalizedMessage = looksLikeHtml
    ? res.status === 403
      ? "You do not have access to this screen."
      : fallbackMessage
    : raw || fallbackMessage;
  const error = new Error(normalizedMessage);
  error.status = res.status;
  if (data?.details && typeof data.details === "object") {
    error.details = data.details;
  }
  return error;
}

export function getAdminIdFromToken() {
  return getCreatorPayloadFromToken()?.created_by ?? null;
}

/** Staff login uses tbl_users id (type user). Admin login uses admin_users id (type admin). */
export function getCreatorPayloadFromToken() {
  const payload = decodeJwtPayload(getAuthItem("jwt_token"));
  if (!payload?.id) return null;
  const tblUserId = payload.tblUserId ?? payload.tbl_user_id;
  if (tblUserId != null && Number.isFinite(Number(tblUserId))) {
    return { created_by: Number(tblUserId), created_by_type: "user" };
  }
  return { created_by: Number(payload.id), created_by_type: "admin" };
}

export async function fetchUserBuilderMasters() {
  const res = await authFetch(`${USER_BUILDER_BASE}/masters`, {
    credentials: "include",
  });
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Failed to load masters");
  return data;
}

const DEFAULT_PAGE_SIZE = 10;

export async function fetchUsersList(
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  filters = {},
) {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const f = filters && typeof filters === "object" ? filters : {};
  const code = String(f.userCode ?? "").trim();
  if (code) params.set("userCode", code);
  const name = String(f.name ?? "").trim();
  if (name) params.set("name", name);
  const email = String(f.email ?? "").trim();
  if (email) params.set("email", email);
  if (f.roleId != null && String(f.roleId).trim() !== "") {
    params.set("roleId", String(f.roleId).trim());
  }
  if (f.userStatusId != null && String(f.userStatusId).trim() !== "") {
    params.set("userStatusId", String(f.userStatusId).trim());
  }
  if (f.attendanceShiftId != null && String(f.attendanceShiftId).trim() !== "") {
    params.set("attendanceShiftId", String(f.attendanceShiftId).trim());
  }
  if (f.attendanceLocationId != null && String(f.attendanceLocationId).trim() !== "") {
    params.set("attendanceLocationId", String(f.attendanceLocationId).trim());
  }
  const contact = String(f.contact ?? "").trim();
  if (contact) params.set("contact", contact);
  const doj = String(f.dateOfJoining ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(doj)) params.set("dateOfJoining", doj);
  const res = await authFetch(`${USER_BUILDER_BASE}/users?${params}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Failed to list users");
  return {
    rows: data.data || [],
    total: data.total ?? 0,
    page: data.page ?? page,
    pageSize: data.pageSize ?? pageSize,
    totalPages: data.totalPages ?? 1,
  };
}

/** Users as select options (inward/outward forms). */
export async function fetchUsersOptions(limit = 500) {
  const cap = Math.min(Math.max(Number(limit) || 500, 1), 2000);
  const { rows } = await fetchUsersList(1, cap, {});
  return (rows || []).map((u) => {
    const name = String(u?.name ?? "").trim();
    const code = String(u?.user_code ?? "").trim();
    const label =
      [name, code].filter(Boolean).join(" — ") || `User #${u?.id ?? ""}`;
    return {
      value: u.id,
      label,
    };
  });
}

function appendUserFormFields(formData, body) {
  Object.entries(body).forEach(([key, value]) => {
    if (value != null && value !== "") {
      formData.append(key, String(value));
    }
  });
}

export async function createUserRequest(body, photoFile = null) {
  const hasPhoto = photoFile instanceof File;
  const init = {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(),
  };

  if (hasPhoto) {
    const formData = new FormData();
    appendUserFormFields(formData, body);
    formData.append("user_photo", photoFile);
    init.body = formData;
    delete init.headers["Content-Type"];
  } else {
    init.body = JSON.stringify(body);
  }

  const res = await authFetch(`${USER_BUILDER_BASE}/user`, init);
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "User create failed");
  return data;
}

export async function updateUserRequest(id, body, photoFile = null) {
  const hasPhoto = photoFile instanceof File;
  const init = {
    method: "PUT",
    credentials: "include",
    headers: buildAuthHeaders(),
  };

  if (hasPhoto) {
    const formData = new FormData();
    appendUserFormFields(formData, body);
    formData.append("user_photo", photoFile);
    init.body = formData;
    delete init.headers["Content-Type"];
  } else {
    init.body = JSON.stringify(body);
  }

  const res = await authFetch(
    `${USER_BUILDER_BASE}/user/${encodeURIComponent(String(id))}`,
    init,
  );
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "User update failed");
  return data;
}

export async function assignUserAttendanceRequest(id, body) {
  const res = await authFetch(
    `${USER_BUILDER_BASE}/user/${encodeURIComponent(String(id))}/attendance-assignment`,
    {
      method: "PUT",
      credentials: "include",
      headers: buildAuthHeaders(),
      body: JSON.stringify(body),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Attendance assignment failed");
  return data;
}

export async function deleteUserRequest(id) {
  const res = await authFetch(
    `${USER_BUILDER_BASE}/user/${encodeURIComponent(String(id))}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: buildAuthHeaders(),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "User delete failed");
  return data;
}

/* ================= ROLES ================= */

export async function fetchRolesList(page = 1, pageSize = 50) {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const res = await authFetch(`${USER_BUILDER_BASE}/roles?${params}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Failed to list roles");
  return {
    rows: data.data || [],
    total: data.total ?? 0,
    page: data.page ?? page,
    pageSize: data.pageSize ?? pageSize,
    totalPages: data.totalPages ?? 1,
  };
}

export async function createRoleRequest(body) {
  const res = await authFetch(`${USER_BUILDER_BASE}/role`, {
    method: "POST",
    credentials: "include",
    headers: buildAuthHeaders(),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Role create failed");
  return data;
}

export async function updateRoleRequest(id, body) {
  const res = await authFetch(
    `${USER_BUILDER_BASE}/role/${encodeURIComponent(String(id))}`,
    {
      method: "PUT",
      credentials: "include",
      headers: buildAuthHeaders(),
      body: JSON.stringify(body),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Role update failed");
  return data;
}

export async function deleteRoleRequest(id) {
  const res = await authFetch(
    `${USER_BUILDER_BASE}/role/${encodeURIComponent(String(id))}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: buildAuthHeaders(),
    },
  );
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Role delete failed");
  return data;
}

/* ================= PERMISSIONS ================= */

export async function fetchModulePermissions(roleId) {
  const params = new URLSearchParams({ role_id: String(roleId) });
  const res = await authFetch(
    `${USER_BUILDER_BASE}/permissions/modules?${params}`,
    { credentials: "include", headers: buildAuthHeaders() },
  );
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Failed to load module permissions");
  return data.data || [];
}

export async function saveModulePermissions(roleId, permissions) {
  const res = await mutatingAuthFetch(`${USER_BUILDER_BASE}/permissions/modules`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role_id: roleId, permissions }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Failed to save module permissions");
  return data;
}

export async function fetchSubmodulePermissions(roleId) {
  const params = new URLSearchParams({ role_id: String(roleId) });
  const res = await authFetch(
    `${USER_BUILDER_BASE}/permissions/submodules?${params}`,
    { credentials: "include", headers: buildAuthHeaders() },
  );
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Failed to load submodule permissions");
  return data.data || [];
}

export async function saveSubmodulePermissions(roleId, permissions) {
  const res = await mutatingAuthFetch(`${USER_BUILDER_BASE}/permissions/submodules`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role_id: roleId, permissions }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw createApiError(res, data, "Failed to save submodule permissions");
  return data;
}
